import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import favorite from "../../../svg/like.svg";
import share from "../../../svg/sharePrimary.svg";
import whatsapp from "../../../svg/whatsappPrimary.svg";
import Favfill from "../../../assets/img/favadd.svg";
import { useDownloadApp } from '../../../Contexts/DownloadAppContext';
import { getMonthNameFromNumber } from '../../../Components/MonthUtils';
import MagazinesCarousel from '../MobileMagazineCarousel';
import { formatMagazineTitle } from '../../../utils/magazineTitleUtils';

/** Custom scrollbar — native thumbs ignore max-height; pill stays ~28px and is drag/touchable */
const FixedThumbScrollPanel: React.FC<{
    children: React.ReactNode;
    thumbColor?: string;
    thumbHeight?: number;
    trackWidth?: number;
}> = ({ children, thumbColor = '#f97316', thumbHeight = 28, trackWidth = 6 }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{ active: boolean; offsetY: number }>({ active: false, offsetY: 0 });
    const [thumbTop, setThumbTop] = useState(0);
    const [showThumb, setShowThumb] = useState(false);
    // Wider hit area for finger targeting on mobile
    const hitWidth = Math.max(trackWidth * 2, 28);
    const pillWidth = trackWidth * 2;

    const getMetrics = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return null;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const maxScroll = Math.max(0, scrollHeight - clientHeight);
        const maxThumbTop = Math.max(0, clientHeight - thumbHeight);
        return { el, scrollTop, scrollHeight, clientHeight, maxScroll, maxThumbTop };
    }, [thumbHeight]);

    const scrollToThumbTop = useCallback((nextThumbTop: number) => {
        const m = getMetrics();
        if (!m || m.maxScroll <= 0 || m.maxThumbTop <= 0) return;
        const clamped = Math.min(m.maxThumbTop, Math.max(0, nextThumbTop));
        m.el.scrollTop = (clamped / m.maxThumbTop) * m.maxScroll;
        setThumbTop(clamped);
    }, [getMetrics]);

    const syncThumb = useCallback(() => {
        if (dragRef.current.active) return;
        const m = getMetrics();
        if (!m) return;
        const canScroll = m.scrollHeight > m.clientHeight + 1;
        setShowThumb(canScroll);
        if (!canScroll) {
            setThumbTop(0);
            return;
        }
        setThumbTop(m.maxScroll > 0 ? (m.scrollTop / m.maxScroll) * m.maxThumbTop : 0);
    }, [getMetrics]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        syncThumb();
        el.addEventListener('scroll', syncThumb, { passive: true });
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncThumb) : null;
        ro?.observe(el);
        const mo = typeof MutationObserver !== 'undefined'
            ? new MutationObserver(syncThumb)
            : null;
        mo?.observe(el, { childList: true, subtree: true, characterData: true });
        return () => {
            el.removeEventListener('scroll', syncThumb);
            ro?.disconnect();
            mo?.disconnect();
        };
    }, [syncThumb, children]);

    // Drag pill (mouse + touch via Pointer Events)
    const onThumbPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        dragRef.current = {
            active: true,
            offsetY: e.clientY - trackRect.top - thumbTop,
        };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onThumbPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current.active) return;
        e.preventDefault();
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        scrollToThumbTop(e.clientY - trackRect.top - dragRef.current.offsetY);
    };

    const onThumbPointerUp = (e: React.PointerEvent) => {
        if (!dragRef.current.active) return;
        dragRef.current.active = false;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            /* already released */
        }
        syncThumb();
    };

    // Tap track to jump (excluding pill drag start)
    const onTrackPointerDown = (e: React.PointerEvent) => {
        if ((e.target as HTMLElement).dataset.thumb === '1') return;
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        scrollToThumbTop(e.clientY - trackRect.top - thumbHeight / 2);
    };

    return (
        <Box sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
        }}>
            <Box
                ref={scrollRef}
                onScroll={syncThumb}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y',
                    pr: showThumb ? `${hitWidth + 4}px` : 1,
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
                }}
            >
                {children}
            </Box>
            {showThumb && (
                <Box
                    ref={trackRef}
                    onPointerDown={onTrackPointerDown}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: `${hitWidth}px`,
                        zIndex: 2,
                        touchAction: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        background: `linear-gradient(to right, transparent ${(hitWidth - trackWidth) / 2}px, rgba(71, 45, 30, 0.12) ${(hitWidth - trackWidth) / 2}px, rgba(71, 45, 30, 0.12) ${(hitWidth - trackWidth) / 2 + trackWidth}px, transparent ${(hitWidth - trackWidth) / 2 + trackWidth}px)`,
                    }}
                >
                    <Box
                        data-thumb="1"
                        onPointerDown={onThumbPointerDown}
                        onPointerMove={onThumbPointerMove}
                        onPointerUp={onThumbPointerUp}
                        onPointerCancel={onThumbPointerUp}
                        sx={{
                            position: 'absolute',
                            top: `${thumbTop}px`,
                            left: `${(hitWidth - pillWidth) / 2}px`,
                            width: `${pillWidth}px`,
                            height: `${thumbHeight}px`,
                            borderRadius: '999px',
                            backgroundColor: thumbColor,
                            cursor: 'grab',
                            touchAction: 'none',
                            '&:active': { cursor: 'grabbing' },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

interface BookDetailsCardProps {
    bookDetails?: any;
    magazinesDetails?: any[];
    totalRecords?: any;
    isLiked: boolean;
    kalyanDetails?: any;
    vivekVaniDetails?: any;
    geetGovindDetails?: any;
    selectedMagazineId?: any;
    magazineLang?: any;
    magazineLanguageId?: any;
    itemType?: string;
    description?: string;
    bookLanguage?: string;
    toc?: any[];
    onToggleLike: () => void;
    /** When true: year/month/edition under cover; Previous Editions + TOC tabs */
    magazineMode?: boolean;
    /** Geet Govind / Kalyan / Kalpataru — drives Previous Editions carousel navigation */
    magazineProductType?: number;
    /** Kalyan/Kalpataru category id — View All opens mMagazineListPage */
    magazineCategoryId?: string;
    magazineCategoryName?: string;
    magazineCategoryLanguage?: any;
}

const BookDetailsCard: React.FC<BookDetailsCardProps> = ({
    bookDetails,
    magazinesDetails,
    isLiked,
    onToggleLike,
    totalRecords,
    kalyanDetails,
    vivekVaniDetails,
    geetGovindDetails,
    selectedMagazineId,
    magazineLang,
    magazineLanguageId,
    itemType,
    description,
    bookLanguage,
    toc,
    magazineMode = false,
    magazineProductType,
    magazineCategoryId,
    magazineCategoryName,
    magazineCategoryLanguage,
}: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setShowDownloadPopup } = useDownloadApp();

    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    type DetailTab = 'editions' | 'description' | 'toc';
    const [activeTab, setActiveTab] = useState<DetailTab>(magazineMode ? 'editions' : 'description');

    // Shared card spacing — magazine mode uses +5px inset and roundness
    const cardRadius = magazineMode ? '10px' : '5px';
    const cardInset = magazineMode ? '10px' : '5px';
    const cardGap = '10px';
    const cardPadding = 17;
    const tabActiveColor = '#f97316';
    const tabMutedColor = '#472d1e';
    const tabBarBg = '#F5F5F5';

    const hasDescription = Boolean(description?.trim()) && !magazineMode;
    const hasToc = Array.isArray(toc) && toc.length > 0;
    const hasEditions = magazineMode && Array.isArray(magazinesDetails) && magazinesDetails.length > 0;
    // Magazine: Previous Editions + TOC; books: Description + TOC
    const visibleTabs = useMemo(() => {
        const tabs: DetailTab[] = [];
        if (hasEditions) tabs.push('editions');
        if (hasDescription) tabs.push('description');
        if (hasToc) tabs.push('toc');
        return tabs;
    }, [hasEditions, hasDescription, hasToc]);
    const showTabsSection = visibleTabs.length > 0;

    // Reset active tab when available tabs change (e.g. after async API load)
    useEffect(() => {
        if (!visibleTabs.includes(activeTab)) {
            setActiveTab(visibleTabs[0] || (magazineMode ? 'editions' : 'description'));
        }
    }, [activeTab, visibleTabs, magazineMode]);

    const getTabLabel = (tabKey: DetailTab): string => {
        if (tabKey === 'editions') return t('previous_editions_tr');
        if (tabKey === 'description') return t('Description_tr');
        return t('Table_of_Contents_tr');
    };

    const renderTocNode = (node: any, index: number | string) => {
        const nodeId = node.navId || `toc-${index}`;
        const isExpanded = expandedNodes.has(nodeId);
        const hasChildren = node.children && node.children.length > 0;

        return (
            <li key={nodeId} style={{ listStyle: 'none', marginBottom: '4px' }}>
                <div
                    onClick={() => hasChildren && setExpandedNodes((prev) => {
                        const next = new Set(prev);
                        if (next.has(nodeId)) next.delete(nodeId);
                        else next.add(nodeId);
                        return next;
                    })}
                    style={{ cursor: hasChildren ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                    <span style={{ fontFamily: 'chanakyaUni', fontSize: '16px' }}>
                        {hasChildren ? (isExpanded ? '− ' : '+ ') : '• '}
                        {node.label || node.title}
                    </span>
                </div>
                {hasChildren && isExpanded && (
                    <ul style={{ paddingLeft: '16px', margin: 0 }}>
                        {node.children.map((child: any, cIdx: number) => renderTocNode(child, `${index}-${cIdx}`))}
                    </ul>
                )}
            </li>
        );
    };

    // Prefer geetGovindDetails when it matches selected id (detail API), else list match, else first
    const selectedFromList = selectedMagazineId && magazinesDetails?.length
        ? magazinesDetails.find((item: any) => String(item?.id) === String(selectedMagazineId))
        : null;
    const detailMatchesSelected = geetGovindDetails && selectedMagazineId
        && String(geetGovindDetails.id) === String(selectedMagazineId);
    const kalyanMatchesSelected = kalyanDetails && selectedMagazineId
        && String(kalyanDetails.id) === String(selectedMagazineId);
    const selectedMagazine = (detailMatchesSelected ? geetGovindDetails : null)
        || (kalyanMatchesSelected ? kalyanDetails : null)
        || selectedFromList
        || geetGovindDetails
        || kalyanDetails
        || (magazinesDetails?.length ? magazinesDetails[0] : null)
        || null;

    // Determine which data source to display
    const dataToDisplay = bookDetails || selectedMagazine || kalyanDetails || vivekVaniDetails || geetGovindDetails;

    if (!dataToDisplay) {
        return <Typography variant="body1">No details available.</Typography>;
    }

    const title = bookDetails?.name || selectedMagazine?.name || kalyanDetails?.name || vivekVaniDetails?.name || geetGovindDetails?.name;
    // Two-line title: Geet Govind uses "("; Kalyan uses वर्ष/year; Kalpataru uses year
    const formattedTitle = formatMagazineTitle(title, magazineProductType);
    // Author card only when API returns a non-empty author string (books mode)
    const author = String(
        bookDetails?.author || selectedMagazine?.author || kalyanDetails?.author || vivekVaniDetails?.author || geetGovindDetails?.author || ''
    ).trim();
    const hasAuthor = Boolean(author) && !magazineMode;

    // Magazine meta: year / month / editionNo (Geet Govind + Kalyan / Kalpataru)
    const magazineYear = selectedMagazine?.years ?? geetGovindDetails?.years ?? kalyanDetails?.years;
    const magazineMonthName = getMonthNameFromNumber(
        selectedMagazine?.months ?? geetGovindDetails?.months ?? kalyanDetails?.months
    );
    const magazineEditionNo = selectedMagazine?.editionNo ?? geetGovindDetails?.editionNo ?? kalyanDetails?.editionNo;
    const hasMagazineMeta = magazineMode && (
        magazineYear != null || magazineMonthName || magazineEditionNo != null
    );
    // Book categories from mobile book detail API — show only when present
    const categories = Array.isArray(bookDetails?.categories)
        ? bookDetails.categories.filter((cat: any) => cat?.name?.trim() && cat?.id)
        : [];
    const language = bookLanguage
        || bookDetails?.bookLanguage
        || bookDetails?.languageName
        || bookDetails?.language
        || selectedMagazine?.magazineLanguage
        || selectedMagazine?.monthlyMagazineLanguage
        || kalyanDetails?.kalyanLanguage
        || kalyanDetails?.kalyanKalpataruLanguage
        || vivekVaniDetails?.vivekVaniLanguage
        || geetGovindDetails?.monthlyMagazineLanguage;
    const thumbnail = bookDetails?.bookThumbPath
        || selectedMagazine?.monthlyMagazineThumbPath
        || kalyanDetails?.kalyanThumbPath
        || kalyanDetails?.kalyanKalpataruThumbPath
        || vivekVaniDetails?.vivekVaniThumbPath
        || geetGovindDetails?.monthlyMagazineThumbPath;

    // Shared surface style for author / language info cards
    const infoCardStyle: React.CSSProperties = {
        backgroundColor: 'white',
        padding: cardPadding,
        fontWeight: 600,
        fontSize: 18,
        borderRadius: cardRadius,
    };

    // Magazine mode: cover + year/month/edition share one white card
    const isCombinedMagazineCard = magazineMode && hasMagazineMeta;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
            gap: cardGap,
            p: cardInset,
            boxSizing: 'border-box',
        }}>
            <Box sx={{
                backgroundColor: "white",
                borderRadius: cardRadius,
                ...(isCombinedMagazineCard
                    ? { display: 'flex', flexDirection: 'column' }
                    : { display: 'flex', height: "204px", padding: '15px', alignItems: 'flex-start' }),
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    ...(isCombinedMagazineCard
                        ? { height: "204px", padding: '15px' }
                        : { flex: 1, width: '100%', minHeight: 0 }),
                }}>
                <Box sx={{
                    width: '120px',
                    height: '180px',
                    flexShrink: 0,
                    mr: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    borderRadius: cardRadius,
                }}>
                    {thumbnail && (
                        <img
                            src={thumbnail}
                            alt={title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: cardRadius }}
                        />
                    )}
                </Box>
                <Box sx={{
                    flexGrow: 1,
                    pt: 0,
                    fontSize: 20,
                    fontWeight: 600,
                    fontFamily: 'chanakyaUni',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                }}>
                    <Box>
                        <Typography variant="h5" sx={{
                            fontSize: 'clamp(22px, 4vw, 20px)',
                            fontWeight: 600,
                            fontFamily: 'chanakyaUni',
                            mb: categories.length > 0 ? 0.5 : 1,
                        }}>
                            {formattedTitle.kind === 'split' ? (
                                <>
                                    {formattedTitle.line1}
                                    <br />
                                    {formattedTitle.line2}
                                </>
                            ) : (formattedTitle.kind === 'fallback' ? title : formattedTitle.text) || title}
                        </Typography>
                        {categories.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.5 }}>
                                {categories.map((category: any) => (
                                    <Typography
                                        key={category.id}
                                        onClick={() => navigate('/books', {
                                            state: {
                                                selectedCategoryId: category.id,
                                                categoryName: category.name,
                                            },
                                        })}
                                        sx={{
                                            fontFamily: 'chanakyaUni',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: tabActiveColor,
                                            cursor: 'pointer',
                                            lineHeight: 1.3,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            '&:active': { opacity: 0.75 },
                                        }}
                                    >
                                        {category.name}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 2 }}>
                        <img
                            src={isLiked ? Favfill : favorite}
                            height={35}
                            width={35}
                            alt="Favorite icon"
                            // onClick={onToggleLike}
                            onClick={()=>setShowDownloadPopup(true)}
                            style={{ cursor: 'pointer' }}
                        />
                        <img src={share} height={35} width={35} alt="Share icon" />
                        <img src={whatsapp} height={35} width={35} alt="Whatsapp icon" />
                    </Box>
                    <Button
                    onClick={()=>{
                        //   navigate(`/reader/books/` + bookDetails?.slug, {
                        //                               state: {
                        //                                 bookDetailId: bookDetails?.id,
                        //                                 bookName: bookDetails?.name,
                        //                                 slug: bookDetails?.slug,
                        //                                 label: bookDetails?.label,
                        //                                 type: BookContentType.vivek,
                        //                               },
                        //                             })    

                        setShowDownloadPopup(true)
                    }}
                        variant='contained'
                        sx={{ backgroundColor: '#f97316', height: 30, width: '100%', borderRadius: cardRadius }}
                    >
                        {t("Read_the_book_tr")}
                    </Button>
                </Box>
                </Box>
                {isCombinedMagazineCard && (
                    <Box sx={{
                        px: `${cardPadding}px`,
                        pb: `${cardPadding}px`,
                        fontWeight: 600,
                        fontSize: 18,
                        fontFamily: 'chanakyaUni',
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                {t("Year_tr")}
                                <br />
                                {magazineYear ?? '—'}
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                {t("Month_tr")}
                                <br />
                                {magazineMonthName || '—'}
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                {t("Number_tr")}
                                <br />
                                {magazineEditionNo ?? '—'}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
            {
                hasAuthor && (
                    <div style={infoCardStyle}>
                        {t("Authors_tr")}
                        <br />
                        {author}
                    </div>
                )
            }

            {
                language &&
                <div style={infoCardStyle}>
                    {t("Language_tr")}
                    <br />
                    {language}
                </div>
            }

            {/* Magazine: Previous Editions + TOC tabs; books: Description + TOC */}
            {showTabsSection && (
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: "white",
                    borderRadius: cardRadius,
                    overflow: 'hidden',
                }}>
                    {/* Tab row — active tab fills full bar height */}
                    <Box sx={{
                        backgroundColor: tabBarBg,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        minHeight: 40,
                    }}>
                        {visibleTabs.map((tabKey) => {
                            const isActive = activeTab === tabKey;

                            return (
                                <Box
                                    key={tabKey}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActiveTab(tabKey)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        px: 1.5,
                                        cursor: 'pointer',
                                        fontFamily: 'chanakyaUni',
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        color: isActive ? tabActiveColor : tabMutedColor,
                                        backgroundColor: isActive ? '#ffffff' : 'transparent',
                                        borderTopLeftRadius: isActive ? cardRadius : 0,
                                        borderTopRightRadius: isActive ? cardRadius : 0,
                                        borderBottom: isActive ? `2px solid ${tabActiveColor}` : '2px solid transparent',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {getTabLabel(tabKey)}
                                </Box>
                            );
                        })}
                    </Box>
                    <Box sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        p: `${cardPadding}px`,
                        backgroundColor: '#ffffff',
                    }}>
                        {activeTab === 'editions' && hasEditions && (
                            <FixedThumbScrollPanel thumbColor={tabActiveColor} thumbHeight={28}>
                                <MagazinesCarousel
                                    magazinesList={magazinesDetails}
                                    totalRecords={totalRecords}
                                    magazineLang={magazineLang}
                                    magazineLanguageId={magazineLanguageId}
                                    itemType={itemType}
                                    magazineProductType={magazineProductType}
                                    magazineCategoryId={magazineCategoryId}
                                    magazineCategoryName={magazineCategoryName}
                                    magazineCategoryLanguage={magazineCategoryLanguage}
                                    t={t}
                                    embedded
                                />
                            </FixedThumbScrollPanel>
                        )}
                        {activeTab === 'description' && hasDescription && (
                            <FixedThumbScrollPanel thumbColor={tabActiveColor} thumbHeight={28}>
                                <div dangerouslySetInnerHTML={{ __html: description }} />
                            </FixedThumbScrollPanel>
                        )}
                        {activeTab === 'toc' && hasToc && (
                            <FixedThumbScrollPanel thumbColor={tabActiveColor} thumbHeight={28}>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {toc.map((node: any, idx: number) => renderTocNode(node, idx))}
                                </ul>
                            </FixedThumbScrollPanel>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default BookDetailsCard;